import axios from 'axios'
import { loginPath } from '../utils/loginSurface'
import { useAuthStore } from '../stores/authStore'
import { useAppStore } from '../stores/appStore'
import { markRequestStart, logRequestFailure, logSlowResponse } from '../utils/netLogger'

const api = axios.create({
  baseURL: '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Aynı anda çok fazla istek (teklif wizard + poll'lar) sunucu threadpool kuyruğuna
// yığılınca hepsi 30sn abort oluyordu. Basit semafor: en fazla N eşzamanlı.
const MAX_INFLIGHT = Number(import.meta.env.VITE_API_MAX_CONCURRENT || 8)
let inflight = 0
const waitQueue = []

function canceledError() {
  const err = new axios.CanceledError('canceled')
  err.code = 'ERR_CANCELED'
  return err
}

function acquireSlot(signal) {
  if (signal?.aborted) return Promise.reject(canceledError())
  if (inflight < MAX_INFLIGHT) {
    inflight += 1
    return Promise.resolve()
  }
  return new Promise((resolve, reject) => {
    const entry = { signal, settled: false, grant: null, fail: null }
    const finish = (fn) => {
      if (entry.settled) return
      entry.settled = true
      signal?.removeEventListener('abort', onAbort)
      fn()
    }
    const onAbort = () => {
      const i = waitQueue.indexOf(entry)
      if (i >= 0) waitQueue.splice(i, 1)
      finish(() => reject(canceledError()))
    }
    entry.grant = () => finish(resolve)
    entry.fail = () => finish(() => reject(canceledError()))
    signal?.addEventListener('abort', onAbort, { once: true })
    waitQueue.push(entry)
  })
}

function releaseSlot() {
  while (waitQueue.length) {
    const next = waitQueue.shift()
    if (!next || next.settled) continue
    if (next.signal?.aborted) { next.fail(); continue }
    next.grant()
    return
  }
  inflight = Math.max(0, inflight - 1)
}

function releaseIfHeld(config) {
  if (config?.__slotHeld) {
    config.__slotHeld = false
    releaseSlot()
  }
}

// Request interceptor: Add auth token and company param
api.interceptors.request.use(
  async (config) => {
    // Ağ logger'ı: isteğin başlangıç anını damgala (performance.now()).
    markRequestStart(config)

    // Arka plan poll'ları kuyrukta kullanıcı aksiyonunu BLOKE ETMESİN —
    // slot beklemeden gider (toast zaten susturulur).
    if (!config.skipConcurrency && !config.silentNetToast) {
      await acquireSlot(config.signal)
      if (config.signal?.aborted) {
        releaseSlot()
        throw canceledError()
      }
      config.__slotHeld = true
    }

    const authStore = useAuthStore.getState()
    const appStore = useAppStore.getState()

    // Add Authorization header
    if (authStore.token) {
      config.headers.Authorization = `Bearer ${authStore.token}`
    }

    // Add company query param
    const company = appStore.activeCompany
    if (config.params) {
      config.params.company = company
    } else {
      config.params = { company }
    }

    return config
  },
  (error) => {
    logRequestFailure(error)
    return Promise.reject(error)
  },
)

// Response interceptor: Handle auth errors and token refresh
api.interceptors.response.use(
  (response) => {
    releaseIfHeld(response.config)
    logSlowResponse(response)   // timeout'a ramak kalan istekler de kayda geçsin
    return response
  },
  async (error) => {
    const originalRequest = error.config
    releaseIfHeld(originalRequest)

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true

      try {
        const authStore = useAuthStore.getState()
        if (authStore.refreshToken) {
          // Attempt to refresh token
          const { data } = await axios.post('/api/v1/auth/refresh', {
            refresh_token: authStore.refreshToken,
          })

          authStore.setTokens(data.access_token, data.refresh_token)

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${data.access_token}`
          return api(originalRequest)
        }
      } catch (refreshError) {
        console.error('Token refresh failed', refreshError)
        useAuthStore.getState().logout()
        // Sabit '/login' DEĞİL: iş ortağı kendi kapısından girdiyse oturumu
        // düşünce iç VERTEX giriş ekranına atılmamalı (utils/loginSurface).
        window.location.href = loginPath()
      }
    }

    // Ağ hatası kaydı — süre, error.code/message, endpoint, tarayıcı (UA).
    // Burada logluyoruz ki 401 → refresh → retry başarıyla toparlandıysa boşuna
    // "hata" yazılmasın; yalnız gerçekten çağırana dönen hatalar kaydedilsin.
    logRequestFailure(error)
    return Promise.reject(error)
  },
)

export default api
